"use client";

import { PropsWithChildren, useEffect, useRef } from "react";

import { getInstantAssistantEnabled, getInstantAssistantVoiceEnabled } from "@/config/feature-flags";
import { useNamespaceFeatureEnabled } from "@/hooks/useNamespaceFeatureFlags";
import { createAssistantSession, openAssistantStream } from "@/lib/assistant-api";
import { useNamespaceStore } from "@/state/namespace-store";
import { useSession } from "@/hooks/useSession";
import type { InstantAssistantMessage } from "@/types/assistant";

import { AssistantHotkeyHandler } from "./components/AssistantHotkeyHandler";
import { InstantAssistantPanel } from "./components/InstantAssistantPanel";
import { loadDiscoveryState, persistDiscoveryState } from "./discovery-storage";
import { persistDraft, persistTranscript, readDraft, readTranscript } from "./storage";
import { deriveDiscoveryUpdateFromUi, mergeDiscoveryUpdates } from "./discovery-state";
import { useInstantAssistantStore } from "./useInstantAssistantStore";
import type { InstantAssistantStore } from "./useInstantAssistantStore";

const GLOW_AUTO_EXPIRE_MS = 7 * 24 * 60 * 60 * 1000;

export function InstantAssistantProvider({ children }: PropsWithChildren) {
  const { session } = useSession();
  const userProfile = session.status === "authenticated" ? session.profile : null;
  const userId = userProfile?.subject ?? null;
  const operatorDisplayName = userProfile?.name?.trim()?.length
    ? userProfile.name!.trim()
    : userProfile?.email?.trim()?.length
      ? userProfile.email!.trim()
      : "Operator";
  const operatorAvatarUrl = userProfile?.pictureUrl ?? null;
  const isAuthenticated = userId !== null;
  const namespaceId = useNamespaceStore((state) => state.selectedNamespaceId);
  const streamStopRef = useRef<null | (() => void)>(null);
  const loadedScopeRef = useRef<{ namespaceId: string | null; userId: string | null }>({
    namespaceId: null,
    userId: null,
  });
  const lastUserRef = useRef<string | null>(null);

  const featureEnabled = useInstantAssistantStore((state) => state.featureEnabled);
  const sessionId = useInstantAssistantStore((state) => state.sessionId);
  const messages = useInstantAssistantStore((state) => state.messages);
  const composerDraft = useInstantAssistantStore((state) => state.composerDraft);
  const manualContextCards = useInstantAssistantStore((state) => state.manualContextCards);
  const discoveryUi = useInstantAssistantStore((state) => state.discoveryUi);
  const operator = useInstantAssistantStore((state) => ({
    id: state.operatorId,
    name: state.operatorName,
    avatarUrl: state.operatorAvatarUrl,
  }));
  const actions = useInstantAssistantStore((state) => state.actions);

  // The assistant is a feature-flagged product feature (experimental tier):
  // the launcher/panel only activates when BOTH the build-time env flag and the
  // per-namespace feature resolution allow it. Resolution is undefined while
  // loading — treated as disabled so a hidden feature never flashes in
  // (docs/feature-flags.md).
  const assistantFeatureResolved = useNamespaceFeatureEnabled("instant-assistant");
  const voiceFeatureResolved = useNamespaceFeatureEnabled("instant-assistant-voice");

  useEffect(() => {
    actions.setFeatureEnabled(getInstantAssistantEnabled() && assistantFeatureResolved === true);
    actions.setVoiceFeatureEnabled(getInstantAssistantVoiceEnabled() && voiceFeatureResolved === true);
  }, [actions, assistantFeatureResolved, voiceFeatureResolved]);

  // Sync operator identity with the authenticated session
  useEffect(() => {
    if (userId) {
      actions.setOperatorProfile({ id: userId, name: operatorDisplayName, avatarUrl: operatorAvatarUrl });
    } else {
      actions.resetOperatorProfile();
    }
  }, [actions, userId, operatorDisplayName, operatorAvatarUrl]);

  // Reset conversation state whenever the active user changes
  useEffect(() => {
    const previous = lastUserRef.current;
    if (previous === userId) {
      return;
    }
    lastUserRef.current = userId;
    if (previous !== null || userId !== null) {
      actions.restartConversation();
      loadedScopeRef.current = { namespaceId: null, userId: null };
      streamStopRef.current?.();
      streamStopRef.current = null;
    }
  }, [actions, userId]);

  // Keep namespace in sync with the global selection
  useEffect(() => {
    actions.setNamespaceId(namespaceId);

    if (!namespaceId || !isAuthenticated) {
      actions.resetState();
      loadedScopeRef.current = { namespaceId: null, userId };
      return;
    }

    if (
      loadedScopeRef.current.namespaceId === namespaceId &&
      loadedScopeRef.current.userId === userId
    ) {
      return;
    }

    loadedScopeRef.current = { namespaceId, userId };
    const activeUserId = userId!;

    (async () => {
      const [history, draft] = await Promise.all([
        readTranscript(namespaceId, activeUserId),
        readDraft(namespaceId, activeUserId),
      ]);

      if (history) {
        actions.prependHistory(history);
      }
      if (draft) {
        actions.setComposerDraft(draft.text ?? "");
        actions.setContextCards(draft.context ?? []);
      }

      const discoveryState = loadDiscoveryState(namespaceId);
      actions.setDiscoveryUi(discoveryState, { persist: false });

      if (
        discoveryState.showGlow &&
        discoveryState.glowFirstSeenAt &&
        Date.now() - Date.parse(discoveryState.glowFirstSeenAt) >= GLOW_AUTO_EXPIRE_MS
      ) {
        actions.setDiscoveryUi({ showGlow: false, showTooltip: false }, { markGlowExpired: true });
      }
    })().catch((error) => {
      console.warn("Failed to hydrate assistant history", error);
    });
  }, [namespaceId, userId, isAuthenticated, actions]);
  // Persist discovery hints
  useEffect(() => {
    if (!namespaceId || !isAuthenticated) {
      return;
    }
    persistDiscoveryState(namespaceId, discoveryUi);
  }, [discoveryUi, namespaceId, isAuthenticated]);


  // Persist transcript when messages change
  useEffect(() => {
    if (!featureEnabled || !namespaceId || messages.length === 0 || !isAuthenticated) {
      return;
    }

    const activeUserId = userId!;
    persistTranscript(namespaceId, messages, activeUserId).catch((error) => {
      console.warn("Unable to persist assistant transcript", error);
    });
  }, [messages, namespaceId, featureEnabled, isAuthenticated, userId]);

  // Persist draft whenever it changes
  useEffect(() => {
    if (!featureEnabled || !namespaceId || !isAuthenticated) {
      return;
    }

    const activeUserId = userId!;
    persistDraft(
      namespaceId,
      {
        text: composerDraft,
        context: manualContextCards,
      },
      activeUserId,
    ).catch((error) => {
      console.warn("Unable to persist assistant draft", error);
    });
  }, [composerDraft, manualContextCards, namespaceId, featureEnabled, isAuthenticated, userId]);

  // Maintain SSE stream. Deliberately omit `messages` from deps — they change
  // on every incoming event, which would force the stream to close/reopen and
  // can cause reconnect storms or uncaught EventSource errors in the middle of
  // a render. The initial `since` cursor is captured once per session.
  useEffect(() => {
    if (!sessionId || !featureEnabled || !isAuthenticated) {
      streamStopRef.current?.();
      streamStopRef.current = null;
      return;
    }

    const initialMessages = useInstantAssistantStore.getState().messages;
    const lastMessage = initialMessages.at(-1);

    streamStopRef.current?.();
    streamStopRef.current = openAssistantStream(
      sessionId,
      {
        onMessage: (message) =>
          handleIncomingMessage(
            message,
            namespaceId,
            userId,
            actions,
            () => useInstantAssistantStore.getState().messages,
          ),
        onQueue: (queue) => actions.setQueue(queue),
        onStatus: (status) => actions.setStatus(status),
        onToolCall: (event) => actions.appendToolCall(event),
        onError: (error) => {
          const message =
            error instanceof Error && error.message ? error.message : "Stream disconnected";
          actions.setError(message);
        },
      },
      { since: lastMessage ? new Date(lastMessage.createdAtUtc) : undefined },
    );

    return () => {
      streamStopRef.current?.();
      streamStopRef.current = null;
    };
  }, [sessionId, featureEnabled, namespaceId, userId, actions, isAuthenticated]);

  // Automatically bootstrap a session when the feature is enabled and namespace exists.
  useEffect(() => {
    if (!featureEnabled || !namespaceId || !isAuthenticated) {
      return;
    }

    if (sessionId) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { discoveryUi: currentDiscoveryUi, pendingDiscoveryUpdate } =
          useInstantAssistantStore.getState();
        const discoveryPayload = mergeDiscoveryUpdates(
          deriveDiscoveryUpdateFromUi(currentDiscoveryUi),
          pendingDiscoveryUpdate,
        );
        const response = await createAssistantSession({
          namespaceId,
          operator: {
            id: operator.id,
            displayName: operator.name,
            avatarUrl: operator.avatarUrl ?? undefined,
          },
          discovery: discoveryPayload ?? undefined,
        });
        if (cancelled) {
          return;
        }
        actions.hydrateSession(response);
        if (discoveryPayload) {
          actions.clearPendingDiscoveryUpdate();
        }
      } catch (error) {
        console.warn("Unable to start assistant session", error);
        actions.setError((error as Error)?.message ?? "Unable to start assistant session");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [featureEnabled, namespaceId, sessionId, operator.id, operator.name, operator.avatarUrl, actions, isAuthenticated]);

  return (
    <>
      {children}
      {featureEnabled ? (
        <>
          <AssistantHotkeyHandler />
          <InstantAssistantPanel />
        </>
      ) : null}
    </>
  );
}

function handleIncomingMessage(
  message: InstantAssistantMessage,
  namespaceId: string | null,
  userId: string | null,
  actions: InstantAssistantStore["actions"],
  getMessages: () => InstantAssistantMessage[],
) {
  actions.appendMessage(message);
  if (namespaceId) {
    persistTranscript(namespaceId, getMessages(), userId).catch((error) => {
      console.warn("Unable to persist assistant transcript", error);
    });
  }
}
